import { ConsolidatedLocationView } from '@mark43/cad-api';
import { getClosestLocation } from '../../common/helpers/get-closest-location';
import { LocationStoreView } from '../../location/types';

export interface ClosestLocationResolver {
    searchLocations: (query: string) => Promise<LocationStoreView[]>;
    resolveEntityLinksForLocation: (
        location: ConsolidatedLocationView
    ) => Promise<ConsolidatedLocationView>;
}

/**
 * A forced result is the picker's "use exactly what I typed" affordance, not a match: it
 * carries the raw query text with no coordinates and no dispatch area. `searchLocations`
 * appends one to every successful search, so when the resolver responds with no real
 * matches it is the only candidate left.
 */
function isForcedResult(location: LocationStoreView): boolean {
    return !!location.isForcedResult || !!location.isForcedSubpremiseResult;
}

/**
 * Resolve a location query — an address or a `"lat,lng"` string for reverse geocoding — to
 * the closest candidate, classified with its dispatch-area entity links.
 *
 * Shared by the First Responder self-initiation GPS prefill and the E911 caller
 * auto-import so both resolve a location the exact same way (`searchLocations` →
 * `getClosestLocation` → `resolveEntityLinksForLocation`).
 *
 * `excludeForcedResults` decides what a no-match search means, because the two callers want
 * opposite things — the difference is whose data the query describes:
 * - First Responder keeps it (the default). The query is the unit's own GPS, so falling back
 *   to the raw coordinates still shows the officer their real position, and "Prefilled with
 *   unit GPS location" is accurate.
 * - The E911 auto-import passes `true`. There the query comes from ANI/ALI data about the
 *   caller, so committing an unresolvable string would tell the calltaker the location
 *   resolved when it did not — a no-match has to surface as a failure instead.
 *
 * Either way a forced result can never outrank a genuine candidate, so any search that finds
 * something behaves identically for both callers.
 *
 * Returns `undefined` when the search yields no usable candidate. If classification fails,
 * the unclassified closest candidate is returned so callers still have a location to write.
 */
export async function resolveClosestClassifiedLocation(
    query: string,
    { searchLocations, resolveEntityLinksForLocation }: ClosestLocationResolver,
    { excludeForcedResults = false }: { excludeForcedResults?: boolean } = {}
): Promise<ConsolidatedLocationView | undefined> {
    const results = await searchLocations(query);
    const candidates = excludeForcedResults
        ? results.filter((result) => !isForcedResult(result))
        : results;
    const closest = getClosestLocation(candidates);
    if (!closest) {
        return undefined;
    }
    try {
        return await resolveEntityLinksForLocation(closest);
    } catch {
        return closest;
    }
}
